import { useState } from 'react';
import toast from 'react-hot-toast';
import { MdOutlineContentCopy } from 'react-icons/md';
import { FiCode, FiChevronUp, FiChevronDown, FiArrowRight } from 'react-icons/fi';
import { SiWordpress, SiSquarespace } from 'react-icons/si';

const ShareAndEmbed = ({ preview, routeToken }) => {
  const formRoute = preview?.route ?? routeToken;
  const shareUrl = `${window.location.origin}/WebForms/${formRoute}`;

  // The snippet users copy — a div + script loader that renders the form as iframe
  const embedSnippet =
    `<div class="crmWebForms" data-crm-webforms="${shareUrl}" style="width:100%;"></div>\n` +
    `<script src="${window.location.origin}/webforms-embed.js" async></script>`;

  const [openSection, setOpenSection] = useState(null);

  const copy = (text, label = 'Copied to clipboard!') => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => toast.success(label));
    } else {
      // Fallback for HTTP / non-secure contexts
      const el = document.createElement('textarea');
      el.value = text;
      el.style.cssText = 'position:fixed;top:-9999px;left:-9999px;opacity:0;';
      document.body.appendChild(el);
      el.focus();
      el.select();
      try {
        document.execCommand('copy');
        toast.success(label);
      } catch {
        toast.error('Copy failed. Please copy manually.');
      }
      document.body.removeChild(el);
    }
  };

  const toggle = (key) => setOpenSection(prev => prev === key ? null : key);

  // ── Shared snippet block ──────────────────────────────────────────────────
  const SnippetBlock = () => (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 overflow-x-auto text-xs font-mono text-slate-700 bg-slate-50 whitespace-pre">
        {embedSnippet}
      </div>
      <div className="flex justify-end px-4 py-2.5 border-t border-slate-200 bg-white">
        <button
          onClick={() => copy(embedSnippet)}
          className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-slate-700 border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
        >
          <MdOutlineContentCopy size={14} />
          Copy to clipboard
        </button>
      </div>
    </div>
  );

  // ── Accordion sections ────────────────────────────────────────────────────
  const sections = [
    {
      key: 'general',
      icon: <FiCode size={16} />,
      title: 'General instructions',
      content: (
        <div className="px-5 pb-5 pt-2 space-y-4">
          <ol className="space-y-3 text-sm text-slate-700">
            <li><span className="font-semibold">1. Copy the code snippet</span> below</li>
            <li>2. Paste the code right where you want the form to appear in your template or CMS editor</li>
          </ol>
          <p className="text-xs text-slate-500 italic leading-relaxed">
            When entering the snippet to your CMS, make sure you are in "code editing" mode.
            For specific instructions for popular website systems, see below.
          </p>
          <SnippetBlock />
        </div>
      ),
    },
    {
      key: 'wordpress-classic',
      icon: <SiWordpress size={15} className="text-slate-500" />,
      title: 'WordPress - Classic editor',
      content: (
        <div className="px-5 pb-5 pt-2 space-y-3 text-sm text-slate-700">
          <ol className="space-y-2">
            <li>1. Open the post or page where you want to embed the form</li>
            <li>2. Switch to <span className="font-semibold">Text</span> mode (not Visual)</li>
            <li>3. Paste the code snippet where you want the form to appear</li>
            <li>4. Switch back to Visual mode and publish/update the page</li>
          </ol>
          <SnippetBlock />
        </div>
      ),
    },
    {
      key: 'wordpress-block',
      icon: <SiWordpress size={15} className="text-slate-500" />,
      title: 'WordPress - Block editor',
      content: (
        <div className="px-5 pb-5 pt-2 space-y-2 text-sm text-slate-700">
          <ol className="space-y-2">
            <li>1. Open the post or page in the Block editor</li>
            <li>2. Add a <span className="font-semibold">Custom HTML</span> block</li>
            <li>3. Paste the code snippet inside the block</li>
            <li>4. Preview or publish the page</li>
          </ol>
          <div className="mt-3"><SnippetBlock /></div>
        </div>
      ),
    },
    {
      key: 'squarespace',
      icon: <SiSquarespace size={14} className="text-slate-500" />,
      title: 'Squarespace',
      content: (
        <div className="px-5 pb-5 pt-2 space-y-2 text-sm text-slate-700">
          <ol className="space-y-2">
            <li>1. Open the page editor in Squarespace</li>
            <li>2. Add a <span className="font-semibold">Code</span> block to your page</li>
            <li>3. Paste the code snippet inside the block</li>
            <li>4. Save and publish the page</li>
          </ol>
          <div className="mt-3"><SnippetBlock /></div>
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 flex flex-col gap-8">

      {/* ── Share as link ─────────────────────────────────────────────────── */}
      <section className="max-w-3xl">
        <h2 className="text-2xl font-semibold text-slate-900 mb-1">Share as link</h2>
        <p className="text-sm text-slate-500 mb-3">Share the link below with your potential clients</p>
        <div className="border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 text-sm text-slate-700 font-mono overflow-x-auto bg-white">
            {shareUrl}
          </div>
          <div className="flex justify-end px-4 py-2.5 border-t border-slate-100 bg-slate-50">
            <button
              onClick={() => copy(shareUrl, 'Link copied!')}
              className="flex items-center gap-2 px-4 py-2 text-xs font-semibold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <MdOutlineContentCopy size={14} />
              Copy to clipboard
            </button>
          </div>
        </div>
      </section>

      <hr className="border-slate-200" />

      {/* ── Embed section ─────────────────────────────────────────────────── */}
      <div className="flex flex-col xl:flex-row gap-8">

        {/* Left: Accordion */}
        <section className="flex-1 min-w-0">
          <h2 className="text-2xl font-semibold text-slate-900 mb-1">Embed the form on your website</h2>
          <p className="text-sm text-slate-500 mb-5">
            Choose how to embed the form on your website or{' '}
            <button className="text-blue-600 hover:underline font-medium inline-flex items-center gap-0.5">
              send the instructions to your colleague <FiArrowRight size={13} />
            </button>
          </p>

          <div className="space-y-2">
            {sections.map(({ key, icon, title, content }) => (
              <div key={key} className="border border-slate-200 rounded-lg overflow-hidden">
                <button
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
                  onClick={() => toggle(key)}
                >
                  <span className="flex items-center gap-2.5 font-semibold text-slate-800 text-sm">
                    {icon} {title}
                  </span>
                  {openSection === key
                    ? <FiChevronUp size={16} className="text-slate-400 shrink-0" />
                    : <FiChevronDown size={16} className="text-slate-400 shrink-0" />
                  }
                </button>
                {openSection === key && content}
              </div>
            ))}
          </div>
        </section>

        {/* Right: Live form preview via iframe */}
        {/* <section className="xl:w-[420px] shrink-0">
          <h2 className="text-2xl font-semibold text-slate-900 mb-1">Form preview</h2>
          <p className="text-sm text-slate-500 mb-5">
            This is how your form looks when embedded on a website.
          </p>
          <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm bg-white">
            Mock browser bar
            <div className="flex items-center gap-1.5 px-4 py-2.5 bg-slate-100 border-b border-slate-200">
              <span className="w-2.5 h-2.5 rounded-full bg-red-400" />
              <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
              <span className="w-2.5 h-2.5 rounded-full bg-green-400" />
              <span className="flex-1 mx-3 bg-white border border-slate-200 rounded text-[10px] text-slate-400 px-2 py-0.5 truncate font-mono">
                {shareUrl}
              </span>
            </div>
            Actual form — auto-height via postMessage from WebFormRoutes
            <iframe
              src={shareUrl}
              title="Form Preview"
              className="w-full border-0 block"
              scrolling="no"
              style={{ height: 500, overflow: 'hidden' }}
              sandbox="allow-scripts allow-same-origin allow-forms"
              onLoad={(e) => {
                const iframe = e.target;
                const handler = (ev) => {
                  try {
                    const data = typeof ev.data === 'string' ? JSON.parse(ev.data) : ev.data;
                    if (data?.type === 'crm-webform-resize' && data.height) {
                      iframe.style.height = data.height + 'px';
                    }
                  } catch (_) {}
                };
                window.addEventListener('message', handler);
              }}
            />
          </div>
        </section> */}

      </div>
    </div>
  );
};

export default ShareAndEmbed;
